<section class="markupRules" [class.markupRulesSidebar]="isSidebarLayout">
  <header class="markupRulesHeader">
    <div class="markupRulesTitleBlock">
      <div class="markupRulesTitleRow">
        <h2>{{ title }}</h2>
        <span class="markupRulesBadge" [class.markupRulesBadgeMuted]="readonly">
          {{ badgeLabel }}
        </span>
      </div>
      <p>{{ subtitle }}</p>
    </div>

    <div class="markupRulesActions">
      <button
        class="markupRulesRefreshAction"
        mat-stroked-button
        type="button"
        (click)="refreshRules.emit()"
        [disabled]="loading || recalculating"
        matTooltip="Обновить список правил"
        aria-label="Обновить список правил"
      >
        <mat-icon>refresh</mat-icon>
        Обновить
      </button>

      <button
        class="markupRulesRecalculateAction"
        mat-stroked-button
        color="primary"
        type="button"
        *ngIf="allowRecalculate && !readonly"
        (click)="confirmRecalculate()"
        [disabled]="loading || recalculating"
        matTooltip="Пересчитать розницу товаров по глобальной таблице"
        aria-label="Пересчитать розницу товаров по глобальной таблице"
      >
        <mat-icon *ngIf="!recalculating">sync</mat-icon>
        <mat-icon *ngIf="recalculating"><mat-spinner [diameter]="18"></mat-spinner></mat-icon>
        Пересчитать розницу
      </button>

      <button
        class="markupRulesCreateAction"
        mat-flat-button
        color="primary"
        type="button"
        *ngIf="!readonly"
        (click)="createRule.emit()"
        [disabled]="loading || recalculating"
        matTooltip="Добавить правило наценки"
        aria-label="Добавить правило наценки"
      >
        <mat-icon>add</mat-icon>
        Добавить правило
      </button>
    </div>
  </header>

  <mat-progress-bar *ngIf="loading || recalculating" mode="indeterminate"></mat-progress-bar>

  <div class="markupRulesNotice" *ngIf="readonly && mode === 'global'">
    Глобальную таблицу можно просматривать всем авторизованным пользователям. Изменения и пересчет доступны только admin.
  </div>

  <div class="markupRulesEmpty" *ngIf="!loading && !hasRules">
    <mat-icon>tune</mat-icon>
    <div>
      <strong>{{ emptyMessage }}</strong>
      <span *ngIf="mode === 'client'">Новые строки документов будут считать цену по глобальной таблице.</span>
    </div>
    <button mat-stroked-button color="primary" type="button" *ngIf="!readonly" (click)="createRule.emit()">
      <mat-icon>add</mat-icon>
      Добавить правило
    </button>
  </div>

  <div class="markupRulesCardList" *ngIf="hasRules && isSidebarLayout">
    <article
      class="markupRuleCard"
      *ngFor="let item of rules; trackBy: trackByRuleId"
      [class.markupRuleCardWarning]="getRuleWarnings(item).length"
    >
      <div class="markupRuleCardTop">
        <div class="markupRuleCardTitle">
          <span>Диапазон закупки</span>
          <strong>{{ formatRange(item) }}</strong>
        </div>

        <div class="markupRuleCardActions" *ngIf="!readonly; else sidebarReadonlyActions">
          <button
            mat-icon-button
            color="primary"
            type="button"
            (click)="editRule.emit(item)"
            [disabled]="loading || recalculating"
            matTooltip="Изменить правило"
            aria-label="Изменить правило"
          >
            <mat-icon>edit</mat-icon>
          </button>
          <button
            mat-icon-button
            color="warn"
            type="button"
            (click)="confirmDelete(item)"
            [disabled]="loading || recalculating"
            matTooltip="Удалить правило"
            aria-label="Удалить правило"
          >
            <mat-icon>delete</mat-icon>
          </button>
        </div>

        <ng-template #sidebarReadonlyActions>
          <span class="markupRulesSecondary">Только просмотр</span>
        </ng-template>
      </div>

      <div class="markupRuleCardMetrics">
        <div class="markupRuleCardMetric">
          <span>Правило</span>
          <strong class="markupRulesRule">{{ formatMarkup(item) }}</strong>
          <small>{{ item.type }}</small>
        </div>

        <div class="markupRuleCardMetric">
          <span>Округление</span>
          <strong>{{ formatRound(item) }}</strong>
        </div>

        <div class="markupRuleCardMetric">
          <span>Приоритет</span>
          <strong>{{ item.priority }}</strong>
        </div>
      </div>

      <div class="markupRuleCardConditions">
        <span class="markupRulesSecondary">Условия</span>
        <div class="markupRulesChips">
          <span class="markupRulesChip markupRulesChipTemplate" *ngIf="getTemplateLabel(item)">
            {{ getTemplateLabel(item) }}
          </span>
          <span class="markupRulesChip" *ngFor="let chip of getConditionChips(item)">
            {{ chip }}
          </span>
        </div>
      </div>

      <div class="markupRuleCardWarningText" *ngIf="getRuleWarnings(item).length">
        <mat-icon>warning</mat-icon>
        {{ getRuleWarnings(item).join(', ') }}
      </div>
    </article>
  </div>

  <div class="markupRulesTableScroll" *ngIf="hasRules && !isSidebarLayout">
    <table
      mat-table
      [dataSource]="rules"
      [trackBy]="trackByRuleId"
      matSort
      [matSortDisabled]="!sortable"
      (matSortChange)="sortChange.emit($event)"
      class="markupRulesTable"
    >
      <ng-container matColumnDef="range">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="rangeStart">Диапазон закупки</th>
        <td mat-cell *matCellDef="let item">
          <div class="markupRulesMainText">{{ formatRange(item) }}</div>
        </td>
      </ng-container>

      <ng-container matColumnDef="rule">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="markup">Правило</th>
        <td mat-cell *matCellDef="let item">
          <div class="markupRulesRule">{{ formatMarkup(item) }}</div>
          <div class="markupRulesSecondary">{{ item.type }}</div>
        </td>
      </ng-container>

      <ng-container matColumnDef="round">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="round">Округление</th>
        <td mat-cell *matCellDef="let item">{{ formatRound(item) }}</td>
      </ng-container>

      <ng-container matColumnDef="conditions">
        <th mat-header-cell *matHeaderCellDef>Условия</th>
        <td mat-cell *matCellDef="let item">
          <div class="markupRulesChips">
            <span class="markupRulesChip markupRulesChipTemplate" *ngIf="getTemplateLabel(item)">
              {{ getTemplateLabel(item) }}
            </span>
            <span class="markupRulesChip" *ngFor="let chip of getConditionChips(item)">
              {{ chip }}
            </span>
            <span
              class="markupRulesWarning"
              *ngIf="getRuleWarnings(item).length"
              [matTooltip]="getRuleWarnings(item).join('\n')"
              matTooltipClass="provider-availability-tooltip"
              aria-label="Предупреждения по правилу"
            >
              <mat-icon>warning</mat-icon>
            </span>
          </div>
        </td>
      </ng-container>

      <ng-container matColumnDef="priority">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="priority">Приоритет</th>
        <td mat-cell *matCellDef="let item">{{ item.priority }}</td>
      </ng-container>

      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef>Действия</th>
        <td mat-cell *matCellDef="let item" class="markupRulesActionCell">
          <ng-container *ngIf="!readonly; else readonlyActions">
            <button
              mat-icon-button
              color="primary"
              type="button"
              (click)="editRule.emit(item)"
              [disabled]="loading || recalculating"
              matTooltip="Изменить правило"
              aria-label="Изменить правило"
            >
              <mat-icon>edit</mat-icon>
            </button>
            <button
              mat-icon-button
              color="warn"
              type="button"
              (click)="confirmDelete(item)"
              [disabled]="loading || recalculating"
              matTooltip="Удалить правило"
              aria-label="Удалить правило"
            >
              <mat-icon>delete</mat-icon>
            </button>
          </ng-container>
          <ng-template #readonlyActions>
            <span class="markupRulesSecondary">Только просмотр</span>
          </ng-template>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr
        mat-row
        *matRowDef="let row; columns: displayedColumns"
        [class.markupRulesRowWarning]="getRuleWarnings(row).length"
      ></tr>
    </table>
  </div>
</section>
